import { fetchComparableDetail } from "@/lib/tobis";
import type { SelectedComparable } from "./tobis-lab-find-comparables";

type ComparableDetail = Awaited<ReturnType<typeof fetchComparableDetail>>;
export type ResolvedComparable = ComparableDetail & { reasoning: string };

// Bound the fan-out on small demo instances. Failed lookups remain explicit:
// they are not evidence that the model selected no comparable films.
export async function loadComparables(selected: readonly SelectedComparable[]): Promise<{
  resolved: ResolvedComparable[];
  failedIds: string[];
}> {
  const results: (ResolvedComparable | null)[] = Array(selected.length).fill(null);
  let next = 0;
  async function worker() {
    while (next < selected.length) {
      const index = next++;
      const selection = selected[index]!;
      try {
        const detail = await fetchComparableDetail(selection.id, AbortSignal.timeout(60_000));
        results[index] = { ...detail, reasoning: selection.reasoning };
      } catch {
        // Caller shows a retry action for the saved result, not a fresh AI run.
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(2, selected.length) }, worker));
  return {
    resolved: results.filter((result): result is ResolvedComparable => result !== null),
    failedIds: selected.filter((_, index) => results[index] === null).map(({ id }) => id),
  };
}
